import { localizedAccountDisplayName } from "../utils/localizedAccountDisplayName";
import type { ParseKeys } from "i18next";
import { useTranslation } from "react-i18next";
// src/pages/ExpenseEntry.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeftIcon,
  XMarkIcon,
  TrashIcon,
  PlusIcon,
} from "@heroicons/react/24/solid";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { Button } from "../components/Button";
import { apiGet, apiPost } from "../api/client";
import { AppleComboBox } from "../components/AppleComboBox";
import { companyRoute } from "../utils/routeHelpers";
import { saveDraft, loadDraft, clearDraft } from "../utils/localStorageDraft";
import {
  readLastPaymentMethod,
  writeLastPaymentMethod,
} from "../utils/lastPaymentMethod";
import { ymdTodayLocal } from "../utils/dateUtils";
import { formatMoney } from "../utils/formatNumber";
import { A } from "../styles/appleClasses";
import { AppleDateInput } from "../components/AppleDateInput";
import { AppleNumberInput } from "../components/AppleNumberInput";
import EditExpenseCategories from "./EditExpenseCategories";
import MemoSection from "../components/MemoSection";
import {
  extractApiDetailOutcome,
  formatClosedThroughDate,
  isClosedPeriodError,
  isDateClosed,
} from "../utils/closedPeriod";
import {
  scannerHandoffFromRouteState,
  type ScannerHandoff,
} from "../types/scannerHandoff";
import {
  fingerprintExpenseCreate,
  randomUuidKey,
  decideExpenseCreateAttempt,
  resolveExpenseCreateSuccess,
  resolveExpenseCreateDefiniteRejection,
  resolveExpenseCreateAmbiguousFailure,
  hydrateExpenseCreateAttempt,
  classifyExpenseCreateFailure,
  IDLE_EXPENSE_CREATE_ATTEMPT,
  type ExpenseCreateAttempt,
  type ExpenseCreateRequest,
} from "../utils/expenseCreateIdempotency";

// Types
type Line = { detail: string; amount: string };

type Account = {
  id: number;
  name: string;
  type: string;
  initial_balance?: number | null;
  kind?: string | null;
  is_system?: boolean | null;
};

type Vendor = {
  id: number;
  name: string;
};

type PaymentSourceOption = {
  kind: "bank" | "on_account";
  id: string; // bank: account id, on_account: "on_account"
  label: string;
};

const defaultLine: Line = { detail: "", amount: "" };
const EXPENSE_DRAFT_KEY = "ricebook_expenseentry_draft";
// Idempotency attempt state, persisted separately from the form draft so an
// ambiguous submission's key survives a remount of /expenses/new (the D2
// audit must not assume a fresh mount) -- see utils/expenseCreateIdempotency.
const EXPENSE_ATTEMPT_KEY = "ricebook_expenseentry_attempt";

function loadPersistedAttempt(): ExpenseCreateAttempt {
  try {
    const raw = localStorage.getItem(EXPENSE_ATTEMPT_KEY);
    return hydrateExpenseCreateAttempt(raw ? JSON.parse(raw) : null);
  } catch {
    return IDLE_EXPENSE_CREATE_ATTEMPT;
  }
}

function persistAttempt(attempt: ExpenseCreateAttempt): void {
  try {
    if (attempt.state === "idle") {
      localStorage.removeItem(EXPENSE_ATTEMPT_KEY);
    } else {
      localStorage.setItem(EXPENSE_ATTEMPT_KEY, JSON.stringify(attempt));
    }
  } catch {
    /* storage unavailable -- in-memory ref still governs this session */
  }
}

// Local display keys are distinct from untranslated server/network messages.
type ExpenseMessage = string | { key: ParseKeys; closedDate?: string | null };
const localMessage = (key: ParseKeys): ExpenseMessage => ({ key });

export default function ExpenseEntry() {
  const { t } = useTranslation();
  const displayMessage = (message: ExpenseMessage): string => typeof message === "string" ? message
    : message.key === "sales.errors.closedSubmit"
      ? t(message.key, { date: message.closedDate ? formatClosedThroughDate(message.closedDate) : t("sales.errors.closedDate") })
      : t(message.key);
  const location = useLocation();
  const navigate = useNavigate();
  const { company_id } = useParams<{ company_id: string }>();
  const [searchParams] = useSearchParams();
  const routeScannerHandoff = useMemo(
    () => scannerHandoffFromRouteState(location.state),
    [location.state],
  );
  const vendorMatchAttemptedRef = useRef(false);
  const skipLastPaymentPreferenceRef = useRef(false);
  const lastPaymentMethodAppliedRef = useRef(false);

  // Idempotency attempt lifecycle. `attemptRef` survives re-renders within a
  // mount and is rehydrated from localStorage on mount (so an ambiguous
  // attempt's key persists across a remount, per the D2 correction). The
  // separate synchronous `inFlightRef` guards a double-click before React
  // re-renders the disabled button -- `saving` state alone updates too late.
  const attemptRef = useRef<ExpenseCreateAttempt>(loadPersistedAttempt());
  const expenseCreateInFlightRef = useRef(false);
  const draftAutosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const draftCompletedRef = useRef(false);

  const stopDraftAutosave = () => {
    draftCompletedRef.current = true;
    if (draftAutosaveTimerRef.current !== null) {
      clearTimeout(draftAutosaveTimerRef.current);
      draftAutosaveTimerRef.current = null;
    }
  };

  const setAttempt = (next: ExpenseCreateAttempt) => {
    attemptRef.current = next;
    persistAttempt(next);
  };
  const returnToRaw = searchParams.get("return_to");

  // Default: Spending hub (Expenses tab)
  const defaultReturnTo = companyRoute(company_id, "/recognition/expenses");
  const returnTo =
    (returnToRaw ? decodeURIComponent(returnToRaw) : null) ?? defaultReturnTo;

  const goBackToCaller = () => {
    clearDraft(EXPENSE_DRAFT_KEY);
    navigate(returnTo, { replace: true });
  };

  const today = ymdTodayLocal();

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [paymentAccounts, setPaymentAccounts] = useState<Account[]>([]);
  const [paymentAccountsLoaded, setPaymentAccountsLoaded] = useState(false);

  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [vendorId, setVendorId] = useState<string>("");

  const [lines, setLines] = useState<Line[]>([{ ...defaultLine }]);
  const [accountId, setAccountId] = useState("");
  const [paymentAccountId, setPaymentAccountId] = useState(""); // bank/cash account id (when kind=bank)

  const [showCategoryInlay, setShowCategoryInlay] = useState(false);

  // payment source control
  const [paymentSourceKind, setPaymentSourceKind] = useState<
    "" | "bank" | "on_account"
  >("");

  const [date, setDate] = useState(today);

  // On Account due date (A/P)
  const [dueDate, setDueDate] = useState(today);
  const [dueDateTouched, setDueDateTouched] = useState(false);

  // Keep state name for backend compatibility; UI shows this as Memo
  const [description, setDescription] = useState("");
  const [error, setError] = useState<ExpenseMessage>("");
  const [saving, setSaving] = useState(false);
  const [appliedScannerHandoff, setAppliedScannerHandoff] =
    useState<ScannerHandoff | null>(null);
  // Keep the submitted source association available even without route state.
  const [sourceDocumentId, setSourceDocumentId] = useState<number | null>(null);
  const [closedThroughDate, setClosedThroughDate] = useState<string | null>(
    null,
  );
  const lower = (s?: string | null) => (s ?? "").toLowerCase();
  const byDisplayName = <T extends { name?: string | null }>(a: T, b: T) =>
    (a.name ?? "").localeCompare(b.name ?? "", undefined, {
      sensitivity: "base",
      numeric: true,
    });

  const sortPaymentAccounts = (accounts: Account[]) => {
    const cash = accounts
      .filter((a) => lower(a.kind) === "cash")
      .sort(byDisplayName);

    const bank = accounts
      .filter((a) => lower(a.kind) === "bank")
      .sort(byDisplayName);

    const creditCard = accounts
      .filter((a) => lower(a.kind) === "credit_card_payable")
      .sort(byDisplayName);

    return [...cash, ...bank, ...creditCard];
  };

  // $ + 2dp, consistent everywhere
  const money = (n: number) => formatMoney(Number(n || 0), "$");

  const labelCls = `${A.label} block mb-1`;

  const iconBtn =
    `${A.iconBtn} inline-flex items-center justify-center ` +
    "hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD";

  const onDateChange = (ymd: string) => {
    setDate(ymd);
    // Keep due date aligned unless user explicitly changed it
    if (paymentSourceKind === "on_account" && !dueDateTouched) {
      setDueDate(ymd);
    }
  };

  const onDueDateChange = (ymd: string) => {
    setDueDateTouched(true);
    setDueDate(ymd);
  };

  const resetExpenseEntry = () => {
    setLines([{ ...defaultLine }]);
    setAccountId("");
    setPaymentAccountId("");
    setVendorId("");
    setPaymentSourceKind("");
    setDate(today);
    setDueDate(today);
    setDueDateTouched(false);
    setDescription("");
    setError("");
    setAppliedScannerHandoff(null);
    setSourceDocumentId(null);
  };

  const applyScannerHandoff = (handoff: ScannerHandoff) => {
    resetExpenseEntry();

    setDate(handoff.dateYmd);
    setDueDate(handoff.dateYmd);
    setLines([
      {
        detail: handoff.vendorText.trim() || "Expense",
        amount: handoff.amount,
      },
    ]);
    vendorMatchAttemptedRef.current = false;
    setAppliedScannerHandoff(handoff);
    setSourceDocumentId(handoff.sourceDocumentId);
  };

  const rememberPaymentSource = (opt: PaymentSourceOption | null) => {
    if (!company_id || !opt) return;

    if (opt.kind === "on_account") {
      writeLastPaymentMethod("expenses", company_id, "on_account");
    } else {
      writeLastPaymentMethod("expenses", company_id, `bank:${opt.id}`);
    }
  };

  // --- Loaders ---
  const loadAccounts = async () => {
    const accs = await apiGet<Account[]>("/accounts");

    // Expense categories (non-system)
    const filtered = accs
      .filter((a) => lower(a.type) === "expense" && !a.is_system)
      .sort((a, b) => a.name.localeCompare(b.name));
    setAccounts(filtered);

    // Payment accounts: cash/bank assets plus the system Credit Card Payable.
    const payAcc = sortPaymentAccounts(
      accs.filter(
        (a) =>
          (lower(a.type) === "asset" &&
            ["cash", "bank"].includes(lower(a.kind)) &&
            !a.is_system) ||
          (lower(a.type) === "liability" &&
            lower(a.kind) === "credit_card_payable"),
      ),
    );
    setPaymentAccounts(payAcc);
    setPaymentAccountsLoaded(true);
  };

  const loadVendors = async () => {
    // Prefer company-scoped vendors if supported; fallback to /vendors
    if (company_id) {
      try {
        const res = await apiGet<Vendor[]>(`/vendors?company_id=${company_id}`);
        setVendors(res || []);
        return;
      } catch {
        // fall through
      }
    }
    try {
      const res = await apiGet<Vendor[]>("/vendors");
      setVendors(res || []);
    } catch {
      setVendors([]);
    }
  };

  const restoreSubmittedPayload = (payload: ExpenseCreateRequest) => {
    setDate(payload.date);
    setAccountId(String(payload.account_id));
    setDescription(payload.description);
    setLines(payload.lines.map(line => ({ detail: line.detail, amount: String(line.amount) })));
    setSourceDocumentId(payload.source_document_id ?? null);
    setPaymentSourceKind(payload.status === "unpaid" ? "on_account" : "bank");
    setVendorId(payload.status === "unpaid" ? String(payload.vendor_id) : "");
    setPaymentAccountId(payload.status === "booked" ? String(payload.payment_account_id) : "");
    setDueDate(payload.status === "unpaid" ? payload.due_date : payload.date);
    setDueDateTouched(payload.status === "unpaid");
    skipLastPaymentPreferenceRef.current = true;
  };

  // Initial load + restore draft
  useEffect(() => {
    loadAccounts();
    loadVendors();

    if (routeScannerHandoff && attemptRef.current.state === "idle") {
      skipLastPaymentPreferenceRef.current = true;
      clearDraft(EXPENSE_DRAFT_KEY);
      applyScannerHandoff(routeScannerHandoff);
    } else {
      const draft = loadDraft(EXPENSE_DRAFT_KEY);
      if (draft) {
        const draftHasPaymentSource = Boolean(
          draft.paymentSourceKind || draft.paymentAccountId || draft.vendorId,
        );
        skipLastPaymentPreferenceRef.current = draftHasPaymentSource;
        setAccountId(draft.accountId ?? "");
        setPaymentAccountId(draft.paymentAccountId ?? "");
        setVendorId(draft.vendorId ?? "");
        setPaymentSourceKind(
          draft.paymentSourceKind ??
            (draft.vendorId
              ? "on_account"
              : draft.paymentAccountId
                ? "bank"
                : ""),
        );

        const restoredDate = draft.date ?? today;
        setDate(restoredDate);

        const restoredDue = draft.dueDate ?? draft.due_date ?? restoredDate;
        setDueDate(restoredDue);
        setDueDateTouched(restoredDue !== restoredDate);

        setDescription(draft.description ?? "");
        setLines(draft.lines?.length ? draft.lines : [{ ...defaultLine }]);
        setSourceDocumentId(draft.sourceDocumentId ??
          attemptRef.current.submittedPayload?.source_document_id ?? null);
      } else if (attemptRef.current.submittedPayload) {
        restoreSubmittedPayload(attemptRef.current.submittedPayload);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!appliedScannerHandoff || vendorMatchAttemptedRef.current) return;
    if (!vendors.length) return;

    vendorMatchAttemptedRef.current = true;
    if (vendorId) return;

    const normalized = appliedScannerHandoff.vendorText.trim().toLowerCase();
    const matches = vendors.filter(
      (vendor) => vendor.name.trim().toLowerCase() === normalized,
    );
    if (matches.length === 1) setVendorId(String(matches[0].id));
  }, [appliedScannerHandoff, vendorId, vendors]);

  useEffect(() => {
    if (!company_id) return;

    apiGet<{
      closed_through_date?: string | null;
    }>(`/settings/financial-year-end?company_id=${company_id}`)
      .then((res) => setClosedThroughDate(res?.closed_through_date || null))
      .catch(() => setClosedThroughDate(null));
  }, [company_id]);

  // Refresh when window regains focus
  useEffect(() => {
    const onFocus = () => {
      loadAccounts();
      loadVendors();
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-save draft (debounced)
  useEffect(() => {
    if (draftCompletedRef.current) return;
    const t = setTimeout(() => {
      draftAutosaveTimerRef.current = null;
      if (draftCompletedRef.current) return;
      saveDraft(EXPENSE_DRAFT_KEY, {
        accountId,
        paymentAccountId,
        vendorId,
        paymentSourceKind,
        date,
        dueDate,
        description,
        lines,
        sourceDocumentId,
      });
    }, 300);
    draftAutosaveTimerRef.current = t;
    return () => clearTimeout(t);
  }, [
    accountId,
    paymentAccountId,
    vendorId,
    paymentSourceKind,
    date,
    dueDate,
    description,
    lines,
    sourceDocumentId,
  ]);

  // Sanitize input: only numbers and one dot
  const sanitizeAmount = (v: string) => {
    v = v.replace(/[^0-9.]/g, "");
    const parts = v.split(".");
    if (parts.length > 2) v = parts[0] + "." + parts.slice(1).join("");
    return v;
  };

  const handleAmountBlur = (idx: number, value: string) => {
    setLines((lines) =>
      lines.map((line, i) => {
        if (i !== idx) return line;
        let v = value;
        if (v.includes(".")) {
          const [int, dec] = v.split(".");
          v = int + (dec ? "." + dec.slice(0, 2) : "");
        }
        if (v.endsWith(".")) v = v.slice(0, -1);
        return { ...line, amount: v };
      }),
    );
  };

  const handleLineChange = (idx: number, field: keyof Line, value: string) => {
    setLines((ls) =>
      ls.map((line, i) =>
        i === idx
          ? {
              ...line,
              [field]: field === "amount" ? sanitizeAmount(value) : value,
            }
          : line,
      ),
    );
  };

  const addLine = () => setLines([...lines, { ...defaultLine }]);
  const removeLine = (idx: number) =>
    setLines((ls) => ls.filter((_, i) => i !== idx));

  const grandTotal = useMemo(
    () => lines.reduce((sum, l) => sum + (parseFloat(l.amount) || 0), 0),
    [lines],
  );

  const isClosedDate = useMemo(() => {
    return isDateClosed(date, closedThroughDate);
  }, [date, closedThroughDate]);

  // Payment Source options: bank/cash + on_account
  const bankOptions: PaymentSourceOption[] = useMemo(
    () =>
      paymentAccounts.map((a) => ({
        kind: "bank" as const,
        id: String(a.id),
        label:
          lower(a.kind) === "credit_card_payable" ? t("expenses.entry.creditCard") : a.name,
      })),
    [paymentAccounts, t],
  );

  const onAccountSourceOption: PaymentSourceOption = useMemo(
    () => ({ kind: "on_account", id: "on_account", label: t("sales.list.onAccount") }),
    [t],
  );

  const paymentSourceOptions: PaymentSourceOption[] = useMemo(
    () => [...bankOptions, onAccountSourceOption],
    [bankOptions, onAccountSourceOption],
  );

  const selectedPaymentSourceOption =
    useMemo<PaymentSourceOption | null>(() => {
      if (paymentSourceKind === "on_account") return onAccountSourceOption;
      if (paymentSourceKind === "bank") {
        return (
          bankOptions.find((o) => o.id === String(paymentAccountId)) || null
        );
      }
      return null;
    }, [paymentSourceKind, onAccountSourceOption, bankOptions, paymentAccountId]);

  const onPaymentSourceChange = (opt: PaymentSourceOption | null) => {
    if (!opt) {
      setPaymentSourceKind("");
      setPaymentAccountId("");
      setVendorId("");
      return;
    }

    rememberPaymentSource(opt);

    if (opt.kind === "on_account") {
      setPaymentSourceKind("on_account");
      setPaymentAccountId("");

      // default due date to expense date
      setDueDate(date);
      setDueDateTouched(false);
      return;
    }
    setPaymentSourceKind("bank");
    setPaymentAccountId(opt.id);
    setVendorId("");
  };

  useEffect(() => {
    if (!company_id) return;
    if (lastPaymentMethodAppliedRef.current) return;
    if (skipLastPaymentPreferenceRef.current) return;
    if (paymentSourceKind) return;

    const remembered = readLastPaymentMethod("expenses", company_id);
    if (!remembered) {
      lastPaymentMethodAppliedRef.current = true;
      return;
    }

    if (remembered === "on_account") {
      setPaymentSourceKind("on_account");
      setPaymentAccountId("");
      setDueDate(date);
      setDueDateTouched(false);
      lastPaymentMethodAppliedRef.current = true;
      return;
    }

    if (remembered.startsWith("bank:")) {
      const rememberedAccountId = remembered.slice("bank:".length);
      const option = bankOptions.find((o) => o.id === rememberedAccountId);
      if (option) {
        setPaymentSourceKind("bank");
        setPaymentAccountId(option.id);
        setVendorId("");
        lastPaymentMethodAppliedRef.current = true;
      }
      if (paymentAccountsLoaded && !option) {
        lastPaymentMethodAppliedRef.current = true;
      }
    }
  }, [
    bankOptions,
    company_id,
    date,
    paymentSourceKind,
    paymentAccountsLoaded,
  ]);

  const selectedVendor = useMemo(
    () => vendors.find((v) => String(v.id) === String(vendorId)) || null,
    [vendors, vendorId],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Synchronous double-submit guard: catches a second submit fired before
    // React re-renders the disabled button. Independent of the attempt state
    // machine below.
    if (expenseCreateInFlightRef.current) return;

    setError("");

    const usingOnAccount = paymentSourceKind === "on_account";
    // Build the actual business body before binding/persisting its identity.
    const payload: ExpenseCreateRequest = {
      date,
      account_id: Number(accountId),
      amount: grandTotal,
      description,
      lines: lines.map(line => ({ detail: line.detail, amount: Number(line.amount) })),
      ...(sourceDocumentId ? { source_document_id: sourceDocumentId } : {}),
      ...(usingOnAccount
        ? { status: "unpaid" as const, vendor_id: Number(vendorId), due_date: dueDate }
        : { status: "booked" as const, payment_account_id: Number(paymentAccountId) }),
    };
    // Even an edit that makes the form invalid must restore the original
    // details rather than bypass the uncertain-attempt mismatch behavior.
    if (attemptRef.current.state === "ambiguous" &&
        attemptRef.current.submittedFingerprint !== fingerprintExpenseCreate(payload)) {
      restoreSubmittedPayload(attemptRef.current.submittedPayload!);
      setError(localMessage("expenses.retry.createAmbiguous"));
      return;
    }

    if (!accountId) return setError(localMessage("expenses.errors.selectCategory"));

    if (lines.some((l) => !l.amount || Number(l.amount) <= 0)) {
      return setError(localMessage("expenses.errors.positiveLines"));
    }

    if (usingOnAccount) {
      if (!vendorId) return setError(localMessage("expenses.errors.onAccountVendor"));
      if (!dueDate) return setError(localMessage("expenses.errors.enterDueDate"));
      if (dueDate < date)
        return setError(localMessage("expenses.errors.dueDate"));
    } else {
      if (!paymentAccountId) {
        return setError(
          localMessage("expenses.errors.selectSource"),
        );
      }
    }

    const decision = decideExpenseCreateAttempt(attemptRef.current, payload, randomUuidKey);
    if (decision.kind === "blocked-in-flight") return;
    if (decision.kind === "blocked-mismatch") {
      restoreSubmittedPayload(attemptRef.current.submittedPayload!);
      setError(localMessage("expenses.retry.createAmbiguous"));
      return;
    }

    expenseCreateInFlightRef.current = true;
    setSaving(true);
    try {
      // Save current fields before the debounce can run, then persist the
      // attempt before POST. The retry sends the stored body, not a rebuild.
      saveDraft(EXPENSE_DRAFT_KEY, {
        accountId, paymentAccountId, vendorId, paymentSourceKind, date, dueDate,
        description, lines, sourceDocumentId,
      });
      setAttempt(decision.attempt);
      await apiPost("/expenses", {
        ...decision.attempt.submittedPayload!,
        idempotency_key: decision.idempotencyKey,
      });

      // Confirmed success (fresh create or idempotent replay -- same response
      // shape): clear the attempt so the next expense gets a fresh key.
      stopDraftAutosave();
      setAttempt(resolveExpenseCreateSuccess());

      if (usingOnAccount) {
        writeLastPaymentMethod("expenses", company_id, "on_account");
      } else {
        writeLastPaymentMethod("expenses", company_id, `bank:${paymentAccountId}`);
      }

      clearDraft(EXPENSE_DRAFT_KEY);
      navigate(returnTo, { replace: true });
    } catch (err: any) {
      const failureClass = classifyExpenseCreateFailure(err, isClosedPeriodError);

      if (failureClass === "closed-period") {
        // Definite pre-commit rejection: assert_year_open raises inside
        // create() before any claim/business write commits, so nothing was
        // persisted. Clear the attempt so a corrected date retries fresh.
        setAttempt(resolveExpenseCreateDefiniteRejection());
        setError({ key: "sales.errors.closedSubmit", closedDate: closedThroughDate });
        return;
      }

      if (failureClass === "idempotency-conflict") {
        // This key is permanently bound to a different completed request.
        setAttempt(resolveExpenseCreateDefiniteRejection());
        setError(localMessage("expenses.retry.createConflict"));
        return;
      }

      if (failureClass === "definite-rejection") {
        // 400/404/422 -- nothing persisted; user can edit and retry fresh.
        setAttempt(resolveExpenseCreateDefiniteRejection());
        const detail = extractApiDetailOutcome(err);
        setError(detail.kind === "raw"
          ? detail.text || localMessage("expenses.errors.create")
          : localMessage("sales.errors.generic"));
        return;
      }

      // Retain the original request and identity. Restore any edits made
      // while POST was pending so the next unchanged retry can replay it.
      setAttempt(resolveExpenseCreateAmbiguousFailure(decision.attempt));
      restoreSubmittedPayload(decision.attempt.submittedPayload!);
      setError(localMessage("expenses.retry.createAmbiguous"));
    } finally {
      expenseCreateInFlightRef.current = false;
      setSaving(false);
    }
  };

  return (
    <div
      className={`min-h-screen bg-gray-50 ${A.body} flex flex-col items-center justify-start px-2 py-8`}
    >
      {/* Header */}
      <div className="sticky top-0 z-30 bg-gray-50 w-full max-w-3xl flex items-center border-b border-gray-100 px-2 pb-2 mb-2">
        <button
          type="button"
          onClick={goBackToCaller}
          className={`${iconBtn} group`}
          aria-label={t("directory.common.back")}
        >
          <ArrowLeftIcon className="w-6 h-6 text-gray-400 group-hover:text-gray-600 transition" />
        </button>

        <h2 className={`${A.title} text-center flex-1 tracking-tight`}>
          {t("expenses.common.new")}
        </h2>

        <button
          type="button"
          onClick={() => {
            clearDraft(EXPENSE_DRAFT_KEY);
            navigate(companyRoute(company_id, "/"));
          }}
          className={`${iconBtn} group`}
          aria-label={t("directory.common.close")}
        >
          <XMarkIcon className="w-6 h-6 text-gray-400 group-hover:text-gray-600 transition" />
        </button>
      </div>

      <form
        onSubmit={handleSubmit}
        className={`${A.card} max-w-3xl w-full p-6 sm:p-8`}
      >
        {appliedScannerHandoff && (
          <div className="mb-4 rounded-2xl border border-appleblue/20 bg-appleblue/5 px-4 py-3 text-[13px] text-gray-600">
            {t("expenses.entry.scanner", { vendor: appliedScannerHandoff.vendorText })}
          </div>
        )}

        {/* Top controls */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-4">
          {/* Category */}
          <div className="relative lg:col-span-2">
            <label className={labelCls}>{t("expenses.common.expenseCategory")}</label>
            <div className="flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <AppleComboBox<Account>
                  emptyText={t("sales.shared.noResults")}
                  toggleOptionsLabel={t("sales.shared.toggleOptions")}
                  options={accounts}
                  value={
                    accounts.find((a) => String(a.id) === accountId) || null
                  }
                  onChange={(acc) => setAccountId(acc ? String(acc.id) : "")}
                  getOptionSearchText={(opt) => opt ? opt.name : ""}
                  getOptionLabel={(opt) => localizedAccountDisplayName(opt, t, opt ? opt.name : "")}
                  placeholder={t("expenses.entry.selectCategory")}
                />
              </div>

              <button
                type="button"
                aria-label={t("expenses.categories.addNew")}
                onClick={() => setShowCategoryInlay(true)}
                className={iconBtn}
                title={t("expenses.categories.addNew")}
              >
                <PlusIcon className="w-5 h-5 text-appleblue" />
              </button>
            </div>
          </div>

          {/* Date */}
          <div>
            <label className={labelCls}>{t("sales.common.date")}</label>
            <AppleDateInput
              openCalendarLabel={t("sales.shared.openCalendar")}
              valueYmd={date}
              onChangeYmd={onDateChange}
              allowEmpty={false}
              ariaLabel={t("sales.common.date")}
              className={A.num}
            />
            {isClosedDate && (
              <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-800">
                {t("sales.errors.closedWarning")}
              </div>
            )}
          </div>
        </div>

        {/* Lines */}
        <div className={`${A.panel} mb-4 p-4`}>
          {/* DESKTOP */}
          <div className="hidden md:block">
            <div
              className={`grid grid-cols-3 border-b border-gray-100 pb-2 mb-2 ${A.label}`}
            >
              <div className="text-gray-500">{t("expenses.common.detail")}</div>
              <div className="text-right pr-1 text-gray-500">{t("sales.common.amount")}</div>
              <div />
            </div>

            {lines.map((line, i) => (
              <div
                key={i}
                className="grid grid-cols-3 gap-2 border-b border-gray-50 py-2"
              >
                <div className="flex-1 min-w-0">
                  <input
                    type="text"
                    className={A.inputBase}
                    value={line.detail}
                    onChange={(e) =>
                      handleLineChange(i, "detail", e.target.value)
                    }
                    placeholder={t("expenses.entry.detailPlaceholder")}
                  />
                </div>

                <div className="flex-1 min-w-0">
                  <AppleNumberInput
                    className={`text-right ${A.num}`}
                    value={line.amount}
                    onChange={(e) =>
                      handleLineChange(i, "amount", e.target.value)
                    }
                    onBlur={(e) => handleAmountBlur(i, e.target.value)}
                    placeholder="0.00"
                    autoComplete="off"
                  />
                </div>

                <div className="md:ml-auto flex items-center justify-end">
                  <button
                    type="button"
                    className={`${
                      A.iconBtn
                    } inline-flex items-center justify-center transition shrink-0 ${
                      lines.length === 1
                        ? "cursor-not-allowed hover:bg-transparent"
                        : "hover:bg-gray-100"
                    }`}
                    onClick={() => removeLine(i)}
                    disabled={lines.length === 1}
                    title={t("sales.common.remove")}
                  >
                    <TrashIcon
                      className={`w-5 h-5 ${
                        lines.length === 1 ? "text-gray-300" : "text-appleblue"
                      }`}
                    />
                  </button>
                </div>
              </div>
            ))}

            <div className="flex flex-wrap gap-2 mt-3 px-1">
              <button
                type="button"
                className="text-[13px] font-normal text-appleblue bg-transparent border-none shadow-none px-0 py-0 hover:text-appleblueD focus:outline-none transition-colors"
                onClick={addLine}
                style={{ alignSelf: "flex-start" }}
              >
                {t("sales.entry.addItem")}
              </button>
            </div>
          </div>

          {/* MOBILE */}
          <div className="md:hidden">
            {lines.map((line, i) => {
              const isLast = i === lines.length - 1;

              return (
                <div
                  key={i}
                  className="mb-3 flex flex-col gap-2 rounded-2xl border border-gray-200 bg-white p-3"
                >
                  <div className="min-w-0">
                    <label className={`block ${A.label} mb-1`}>{t("expenses.common.detail")}</label>
                    <input
                      type="text"
                      className={A.inputBase}
                      value={line.detail}
                      onChange={(e) =>
                        handleLineChange(i, "detail", e.target.value)
                      }
                      placeholder={t("expenses.entry.detailPlaceholder")}
                    />
                  </div>

                  <div className="min-w-0">
                    <label className={`block ${A.label} mb-1`}>{t("sales.common.amount")}</label>
                    <AppleNumberInput
                      className={`${A.inputBase} text-right font-medium ${A.num} appearance-none`}
                      value={line.amount}
                      onChange={(e) =>
                        handleLineChange(i, "amount", e.target.value)
                      }
                      onBlur={(e) => handleAmountBlur(i, e.target.value)}
                      placeholder="0.00"
                      autoComplete="off"
                    />
                  </div>

                  <div
                    className={`mt-2 flex items-center ${
                      isLast ? "justify-between" : "justify-end"
                    }`}
                  >
                    {isLast ? (
                      <button
                        type="button"
                        className="text-[13px] font-normal text-appleblue bg-transparent border-none shadow-none px-0 py-0 hover:text-appleblueD focus:outline-none transition-colors"
                        onClick={addLine}
                      >
                        {t("sales.entry.addItem")}
                      </button>
                    ) : (
                      <div />
                    )}

                    <button
                      type="button"
                      className={`${
                        A.iconBtn
                      } inline-flex items-center justify-center transition shrink-0 ${
                        lines.length === 1
                          ? "cursor-not-allowed hover:bg-transparent"
                          : "hover:bg-gray-100"
                      }`}
                      onClick={() => removeLine(i)}
                      disabled={lines.length === 1}
                      title={t("sales.common.remove")}
                    >
                      <TrashIcon
                        className={`w-5 h-5 ${
                          lines.length === 1
                            ? "text-gray-300"
                            : "text-appleblue"
                        }`}
                      />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Grand Total */}
        <div className="bg-gray-50 border border-gray-100 rounded-2xl p-4 mb-4 flex justify-end">
          <div className="text-right">
            <div className={A.label}>{t("sales.common.grandTotal")}</div>
            <div className={`${A.title} ${A.num}`}>{money(grandTotal)}</div>
          </div>
        </div>

        {/* Payment */}
        <div className="mb-4 pt-3 border-t border-gray-100">
          <div className={`${A.label} mb-2`}>{t("sales.common.payment")}</div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative md:col-span-2">
              <label className={labelCls}>{t("expenses.entry.paymentSource")}</label>
              <AppleComboBox<PaymentSourceOption>
                emptyText={t("sales.shared.noResults")}
                toggleOptionsLabel={t("sales.shared.toggleOptions")}
                options={paymentSourceOptions}
                value={selectedPaymentSourceOption}
                onChange={onPaymentSourceChange}
                getOptionLabel={(opt) => (opt ? opt.label : "")}
                placeholder={
                  paymentSourceOptions.length
                    ? t("expenses.entry.selectSource")
                    : t("expenses.entry.noSources")
                }
              />
            </div>

            {paymentSourceKind === "on_account" && (
              <>
                <div className="relative">
                  <label className={labelCls}>{t("directory.vendors.singular")}</label>
                  <AppleComboBox<Vendor>
                    emptyText={t("sales.shared.noResults")}
                    toggleOptionsLabel={t("sales.shared.toggleOptions")}
                    options={vendors}
                    value={selectedVendor}
                    onChange={(v) => setVendorId(v ? String(v.id) : "")}
                    getOptionLabel={(v) => (v ? v.name : "")}
                    placeholder={
                      vendors.length ? t("purchases.entry.selectVendor") : t("expenses.entry.noVendors")
                    }
                  />
                  <div className={`mt-1 ${A.label}`}>{t("expenses.entry.addsPayables")}</div>
                </div>

                <div>
                  <label className={labelCls}>
                    {t("sales.common.dueDate")} <span className="text-red-500">*</span>
                  </label>
                  <AppleDateInput
                    openCalendarLabel={t("sales.shared.openCalendar")}
                    valueYmd={dueDate}
                    onChangeYmd={onDueDateChange}
                    allowEmpty={false}
                    ariaLabel={t("sales.common.dueDate")}
                    className={A.num}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Memo */}
        <div className="mb-4">
          <MemoSection
            label={t("sales.common.memo")}
            placeholder={t("sales.common.memoPlaceholder")}
            mode="edit"
            value={description}
            onChange={setDescription}
            className="mt-4 mb-8"
          />
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-6">
          <Button variant="secondary" type="button" onClick={goBackToCaller}>
            {t("directory.common.cancel")}
          </Button>
          <Button type="submit" variant="primary" disabled={saving}>
            {saving ? t("directory.common.savingDots") : t("sales.common.enter")}
          </Button>
        </div>

        {/* Error */}
        <div style={{ minHeight: 24 }} className="mt-2">
          {error && (
            <div className={`${A.body} text-red-600 font-medium`}>{displayMessage(error)}</div>
          )}
        </div>
      </form>

      {/* Category Inlay (outside the form, like SaleEntry) */}
      {showCategoryInlay && (
        <EditExpenseCategories
          mode="inlay"
          title={t("expenses.categories.newCategory")}
          companyId={company_id}
          onClose={() => setShowCategoryInlay(false)}
          onCreated={(cat) => {
            const idStr = String(cat.id);

            // Optimistic add so dropdown updates instantly
            setAccounts((prev) => {
              if (prev.some((a) => String(a.id) === idStr)) return prev;
              return [...prev, cat].sort((a, b) =>
                a.name.localeCompare(b.name),
              );
            });

            // Select it
            setAccountId(idStr);

            // Close + refresh from server truth
            setShowCategoryInlay(false);
            loadAccounts();
          }}
        />
      )}
    </div>
  );
}
